import { ActionIcon, Badge, Box, Button, Group, Stack, Text, Title, Tooltip } from '@mantine/core';
import { IconAdjustmentsHorizontal, IconChevronLeft, IconChevronRight, IconMessage } from '@tabler/icons-react';
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { useSeja } from '../seja/useSeja';
import { barve } from '../tema';
import { obdobje } from '../oblika/oblika';
import { HITRA_DEJANJA, vidnaDejanja } from './hitraDejanja';
import { ObvestilaZacetne } from './ObvestilaZacetne';
import { PregledPoslovanja } from './PregledPoslovanja';
import { sme } from '../seja/pravice';
import { PrilagoditevZacetne } from './PrilagoditevZacetne';
import { PRIVZETA_DEJANJA, useNastavitveZacetne, useShraniNastavitveZacetne } from './zacetna';
import { useSteviloPovprasevanj } from '../povprasevanja/povprasevanja';
import { noviOdgovori, useNeprebraniOdgovori } from '../povratne/povratne';
import { useDelavnica, useProdaja } from '../seja/paket';
import { PregledDelavnice } from '../delavnica/PregledDelavnice';

const IKONA = { size: 20, stroke: 1.8 };

/**
 * Začetni zaslon po prijavi: opozorila in novica, vrstica hitrih dejanj (na telefonu se vrstica vodoravno pomika, puščici ob robu pokažeta, da je še kaj skrito), pod njo pregled poslovanja ({@link PregledPoslovanja}) in povratne
 * informacije (z oznako novih odgovorov naše ekipe). »Prilagodi« odpre okno z vsemi osebnimi nastavitvami zaslona
 * ({@link PrilagoditevZacetne}: vrstni red in skrivanje hitrih dejanj, številke, kartice), ki jih hrani strežnik
 * ({@link useNastavitveZacetne}). Brez vseh funkcionalnosti instance (zastavica na strežniku) so hitra dejanja
 * onemogočena in pregleda ni. Dejanja in pregled modula, ki ga paket nima, se ne pokažejo; z modulom
 * Delavnica je nad pregledom poslovanja pregled delavnice ({@link PregledDelavnice}).
 */
export function Zacetna() {
  const stanje = useSeja();
  const vse = stanje.status !== 'prijavljen' || stanje.instanca.vseFunkcionalnosti !== false;
  const prodaja = useProdaja();
  const delavnica = useDelavnica();
  const ime = stanje.status === 'prijavljen' ? stanje.uporabnik.ime : undefined;
  const podjetje = stanje.status === 'prijavljen' ? stanje.instanca.naziv : undefined;
  const odgovori = Number(useNeprebraniOdgovori().data?.neprebrani ?? 0);
  const povprasevanja = Number(useSteviloPovprasevanj().data?.nova ?? 0);
  const nastavitve = useNastavitveZacetne();
  const shrani = useShraniNastavitveZacetne();
  const [prilagajanje, setPrilagajanje] = useState(false);
  const danes = new Date();
  const mesec = `${danes.getFullYear()}-${String(danes.getMonth() + 1).padStart(2, '0')}`;
  const red = nastavitve?.hitraDejanja ?? PRIVZETA_DEJANJA;
  const pravice = stanje.status === 'prijavljen' ? stanje.pravice : undefined;
  const vidna = vidnaDejanja(red, nastavitve?.skritaDejanja ?? [], prodaja, delavnica, PRIVZETA_DEJANJA, pravice);

  return (
    <Box
      m="calc(-1 * var(--mantine-spacing-md))"
      mih="calc(100dvh - var(--app-shell-header-height, 60px))"
      px={{ base: 'md', sm: 'xl' }}
      py={{ base: 'md', sm: 'xl' }}
      style={{
        background: `linear-gradient(180deg, ${barve.ozadje} 0%, ${barve.ozadjeTemno} 100%)`,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Stack gap="md" w="100%" maw={1400} mx="auto" style={{ flex: 1 }}>
        <ObvestilaZacetne />
        <Group justify="space-between" align="flex-end" gap="sm" wrap="nowrap">
          <div style={{ minWidth: 0 }}>
            <Title order={1} size="h2" c="white">
              {ime ? `Pozdravljeni, ${ime}` : 'Pozdravljeni'}
            </Title>
            <Text c={barve.besediloNaOzadjuSekundarno} size="sm">
              {[podjetje, obdobje(mesec)].filter(Boolean).join(' · ')}
            </Text>
          </div>
          {vse && nastavitve && (
            <>
              <Button
                visibleFrom="sm"
                variant="subtle"
                color="gray.0"
                size="compact-md"
                leftSection={<IconAdjustmentsHorizontal size={18} />}
                aria-label="Prilagodi začetno stran"
                onClick={() => setPrilagajanje(true)}
                style={{ flexShrink: 0 }}
              >
                Prilagodi
              </Button>
              <Tooltip label="Prilagodi začetno stran">
                <ActionIcon
                  hiddenFrom="sm"
                  variant="subtle"
                  color="gray.0"
                  size={40}
                  aria-label="Prilagodi začetno stran"
                  onClick={() => setPrilagajanje(true)}
                  style={{ flexShrink: 0 }}
                >
                  <IconAdjustmentsHorizontal size={20} />
                </ActionIcon>
              </Tooltip>
            </>
          )}
        </Group>
        {/* do nastavitev s strežnika neviden (prostor ostane), da gumbi ne preskočijo v shranjeni vrstni red */}
        <Box style={{ visibility: nastavitve ? undefined : 'hidden' }}>
          <VodoravniTrak oznaka="Hitra dejanja">
            {vidna.map((k) => {
              const { naziv, Ikona, na } = HITRA_DEJANJA[k];
              return (
                <HitroDejanje
                  key={k}
                  naziv={naziv}
                  ikona={<Ikona {...IKONA} />}
                  na={na}
                  onemogoceno={!vse}
                  stevec={k === 'POVPRASEVANJA' ? povprasevanja : undefined}
                />
              );
            })}
          </VodoravniTrak>
        </Box>
        {nastavitve && (
          <PrilagoditevZacetne
            nastavitve={nastavitve}
            prodaja={prodaja}
            delavnica={delavnica}
            odprto={prilagajanje}
            zapri={() => setPrilagajanje(false)}
            shrani={(n) => shrani.mutate(n)}
          />
        )}
        {!vse ? (
          <Text c="white" ta="center" maw={640} mx="auto" py="xl">
            Za to podjetje so funkcije programa trenutno omejene. Za več informacij nas kontaktirajte.
          </Text>
        ) : (
          <>
            {delavnica && sme(pravice, 'DELAVNICA') && <PregledDelavnice />}
            {prodaja && sme(pravice, 'ANALIZE') && (
              <PregledPoslovanja nastavitve={nastavitve} shrani={(n) => shrani.mutate(n)} />
            )}
          </>
        )}
      </Stack>
      <Group mt="xl" w="100%" maw={1400} mx="auto">
        <Button
          component={Link}
          to="/povratne-informacije"
          variant="outline"
          color="gray.0"
          leftSection={<IconMessage size={18} />}
          rightSection={
            odgovori > 0 ? (
              <Badge color="red" variant="filled" size="sm">
                {noviOdgovori(odgovori)}
              </Badge>
            ) : undefined
          }
        >
          Povratne informacije
        </Button>
      </Group>
    </Box>
  );
}

/**
 * Vodoravno pomična vrstica (na telefonu do roba zaslona). Kadar je na eni strani še kaj skrito, je tam zbledel rob s
 * puščico, ki vrstico premakne, da je vedno vidno, da se jo da pomikati – tudi ko gumb konča ravno ob robu zaslona.
 */
function VodoravniTrak({ oznaka, children }: { oznaka: string; children: ReactNode }) {
  const trak = useRef<HTMLDivElement>(null);
  const [skrito, setSkrito] = useState({ levo: false, desno: false });
  const posodobi = useCallback(() => {
    const t = trak.current;
    if (t) {
      setSkrito({ levo: t.scrollLeft > 2, desno: t.scrollLeft + t.clientWidth < t.scrollWidth - 2 });
    }
  }, []);
  useEffect(() => {
    posodobi();
    const t = trak.current;
    if (!t || typeof ResizeObserver === 'undefined') {
      return;
    }
    const opazovalec = new ResizeObserver(posodobi);
    opazovalec.observe(t);
    if (t.firstElementChild) {
      opazovalec.observe(t.firstElementChild);
    }
    return () => opazovalec.disconnect();
  }, [posodobi]);
  const premakni = (smer: 1 | -1) =>
    trak.current?.scrollBy({ left: smer * trak.current.clientWidth * 0.7, behavior: 'smooth' });

  return (
    <Box pos="relative" mx={{ base: 'calc(-1 * var(--mantine-spacing-md))', sm: 0 }}>
      <Box
        ref={trak}
        component="nav"
        aria-label={oznaka}
        onScroll={posodobi}
        px={{ base: 'md', sm: 0 }}
        style={{ overflowX: 'auto', scrollbarWidth: 'none' }}
      >
        <Group gap="sm" wrap="nowrap" pb={4} w="max-content">
          {children}
        </Group>
      </Box>
      {skrito.levo && <Puscica smer={-1} onClick={() => premakni(-1)} />}
      {skrito.desno && <Puscica smer={1} onClick={() => premakni(1)} />}
    </Box>
  );
}

/** Zbledel rob z okroglo puščico; gumbi so dosegljivi tudi s tipkovnico, zato puščica ni v vrstnem redu fokusa. */
function Puscica({ smer, onClick }: { smer: 1 | -1; onClick: () => void }) {
  return (
    <Box
      data-puscica={smer > 0 ? 'desno' : 'levo'}
      style={{
        position: 'absolute',
        top: 0,
        bottom: 4,
        [smer > 0 ? 'right' : 'left']: 0,
        width: 64,
        display: 'flex',
        alignItems: 'center',
        justifyContent: smer > 0 ? 'flex-end' : 'flex-start',
        paddingInline: 6,
        pointerEvents: 'none',
        background: `linear-gradient(${smer > 0 ? 'to left' : 'to right'}, ${barve.ozadje} 35%, transparent)`,
      }}
    >
      <ActionIcon
        variant="white"
        color="hera"
        radius="xl"
        size={34}
        onClick={onClick}
        tabIndex={-1}
        aria-label={smer > 0 ? 'Več dejanj' : 'Prejšnja dejanja'}
        style={{ pointerEvents: 'auto', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)' }}
      >
        {smer > 0 ? <IconChevronRight size={20} stroke={2.2} /> : <IconChevronLeft size={20} stroke={2.2} />}
      </ActionIcon>
    </Box>
  );
}

/** Gumb v vrstici hitrih dejanj (bel na temnem ozadju); {@code stevec} = število novih v rdečem krogcu. */
function HitroDejanje({
  naziv,
  ikona,
  na,
  onemogoceno,
  stevec,
}: {
  naziv: string;
  ikona: ReactNode;
  na: string;
  onemogoceno: boolean;
  stevec?: number;
}) {
  return (
    <Button
      component={Link}
      to={na}
      variant="white"
      color="hera"
      size="md"
      radius="md"
      leftSection={ikona}
      rightSection={
        stevec ? (
          <Badge color="red" circle size="md">
            {stevec}
          </Badge>
        ) : undefined
      }
      aria-label={stevec ? `${naziv} (novo: ${stevec})` : naziv}
      aria-disabled={onemogoceno || undefined}
      tabIndex={onemogoceno ? -1 : undefined}
      onClick={(e: React.MouseEvent) => onemogoceno && e.preventDefault()}
      style={{ flexShrink: 0, opacity: onemogoceno ? 0.55 : 1, cursor: onemogoceno ? 'not-allowed' : undefined }}
    >
      {naziv}
    </Button>
  );
}
